import { memo, useMemo } from 'react';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, currencyFmt } from './shared';
import type { BranchControlConfig } from '../../types/panelConfig';

interface Props {
  config: BranchControlConfig;
  summarySourceId: number | null | undefined;
  kasaSourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

const numberFmt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

interface Branch {
  magaza: string;
  ciro: number;
  fis: number;
  pos: number;
  nakit: number;
}

interface KasaRow {
  magaza: string;
  kasaAd: string;
  giris: number;
  cikis: number;
  net: number;
}

function num(v: unknown): number {
  return Number(v) || 0;
}

// One <details> accordion per şube: header shows magaza + dönem ciro; the open
// body holds 3 KPIs (Ciro/Fiş/Sepet ort.), a POS/Nakit payment-mix bar, and a
// kasa table. summary rows = branches; kasa rows joined by magaza.
function BranchControlWidgetImpl({ config, summarySourceId, kasaSourceId, parameters }: Props) {
  const { groupField = 'magaza' } = config;
  const summary = useWidgetData(summarySourceId, `branch-summary-${config.summaryReportCode}`, parameters);
  const kasa = useWidgetData(kasaSourceId, `branch-kasa-${config.kasaReportCode}`, parameters);

  const branches = useMemo<Branch[]>(() => {
    return (summary.data ?? [])
      .map((r) => ({
        magaza: String(r[groupField] ?? ''),
        ciro: num(r.ciro),
        fis: num(r.fis_sayisi),
        pos: num(r.pos_tutar),
        nakit: num(r.nakit_tutar),
      }))
      .sort((a, b) => b.ciro - a.ciro);
  }, [summary.data, groupField]);

  const kasaByBranch = useMemo<Map<string, KasaRow[]>>(() => {
    const map = new Map<string, KasaRow[]>();
    for (const r of kasa.data ?? []) {
      const magaza = String(r[groupField] ?? '');
      const row: KasaRow = {
        magaza,
        kasaAd: String(r.kasa_ad ?? ''),
        giris: num(r.giris),
        cikis: num(r.cikis),
        net: num(r.net_hareket),
      };
      const list = map.get(magaza) ?? [];
      list.push(row);
      map.set(magaza, list);
    }
    return map;
  }, [kasa.data, groupField]);

  const isLoading = summary.isLoading || kasa.isLoading;

  return (
    <div data-testid="branch-control-widget">
      <Panel title={config.title} subtitle={config.subtitle}>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : branches.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {branches.map((b, i) => {
              const sepet = b.fis > 0 ? b.ciro / b.fis : 0;
              const total = b.pos + b.nakit;
              const posPct = total > 0 ? Math.round((b.pos / total) * 100) : 0;
              const nakitPct = total > 0 ? Math.round((b.nakit / total) * 100) : 0;
              const rows = kasaByBranch.get(b.magaza) ?? [];
              return (
                <details
                  key={b.magaza}
                  open={i === 0}
                  style={{
                    border: '1px solid var(--color-border)',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <summary
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      listStyle: 'none',
                      background: 'var(--color-bg-subtle, #f8fafc)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                      {b.magaza}
                    </span>
                    <span
                      className="tnum"
                      style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}
                    >
                      {currencyFmt.format(b.ciro)}
                    </span>
                  </summary>

                  <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* 3 KPI */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      <Kpi label="Ciro" value={currencyFmt.format(b.ciro)} />
                      <Kpi label="Fiş" value={numberFmt.format(b.fis)} />
                      <Kpi label="Sepet ort." value={currencyFmt.format(sepet)} />
                    </div>

                    {/* POS / Nakit payment mix */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <Bar label="POS" amount={b.pos} pct={posPct} fill="var(--color-primary)" />
                      <Bar label="Nakit" amount={b.nakit} pct={nakitPct} fill="var(--color-positive)" />
                    </div>

                    {/* Kasa table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ color: 'var(--color-text-muted)', textAlign: 'left' }}>
                          <th style={thStyle}>Kasa</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Giriş</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Çıkış</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((k) => (
                          <tr key={`${b.magaza}-${k.kasaAd}`} style={{ borderTop: '1px solid var(--color-border)' }}>
                            <td style={tdStyle}>{k.kasaAd}</td>
                            <td className="tnum" style={{ ...tdStyle, textAlign: 'right' }}>
                              {currencyFmt.format(k.giris)}
                            </td>
                            <td className="tnum" style={{ ...tdStyle, textAlign: 'right' }}>
                              {currencyFmt.format(k.cikis)}
                            </td>
                            <td
                              className="tnum"
                              style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}
                            >
                              {currencyFmt.format(k.net)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}

const thStyle: React.CSSProperties = { padding: '6px 8px', fontWeight: 600, fontSize: 11 };
const tdStyle: React.CSSProperties = { padding: '6px 8px', color: 'var(--color-text-primary)' };

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </span>
      <span
        className="tnum"
        style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}
      >
        {value}
      </span>
    </div>
  );
}

function Bar({ label, amount, pct, fill }: { label: string; amount: number; pct: number; fill: string }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
        <span
          className="tnum"
          style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}
        >
          {currencyFmt.format(amount)}
        </span>
        <span
          className="tnum"
          style={{ marginLeft: 10, fontSize: 12.5, color: 'var(--color-text-muted)', minWidth: 38, textAlign: 'right' }}
        >
          %{pct}
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 5, background: 'var(--color-border-light, #eef1f5)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 5, background: fill, transition: 'width .3s ease' }} />
      </div>
    </div>
  );
}

export const BranchControlWidget = memo(BranchControlWidgetImpl);
